/**
 * Pure helpers for reviewing a multicam project on one shared timeline.
 *
 * Timeline time is the master's clock. An angle's source position is
 *   sourceMs = timelineMs - timelineStartMs + sourceInMs
 * and it has footage while sourceInMs <= sourceMs <= (sourceOutMs ?? durationMs).
 *
 * A duration of 0 (or missing) means "not recorded", not "no footage": some
 * snapshots were saved before the angle's length was known. Until the
 * element's real length arrives (loadedmetadata), such an angle is treated as
 * open-ended rather than covering nothing, which used to black out the angle
 * and stop it ever being played.
 */

import { brand } from '../../theme';
import { DEFAULT_CATEGORY_ID, getAnnotationCategory } from '../../utils/annotationCategories';
import { angleColor } from './multicamTheme';

export interface ReviewAngle {
  angleKey: string;
  label?: string | null;
  videoUrl: string;
  videoName?: string | null;
  durationMs: number;
  timelineStartMs: number;
  sourceInMs: number;
  sourceOutMs?: number | null;
}

export interface ReviewAnnotation {
  timelineTimeMs: number;
  endTimeMs?: number | null;
  type: string;
  targetAngleKey?: string | null;
  text?: string | null;
  category?: string | null;
  color?: string | null;
}

/** Cuts without a target angle. */
export const SWITCH_COLOR = brand.green;
export const NOTE_COLOR = getAnnotationCategory(DEFAULT_CATEGORY_ID).color;

export const isSwitch = (annotation: ReviewAnnotation) =>
  (annotation.type || '').toUpperCase() === 'SWITCH';

/**
 * A cut takes the colour of the angle it cuts to (as in the multicam studio's
 * angles lane); a note takes its shared category colour, so notes saved with
 * the old multicam palette match everywhere else.
 */
export const annotationColor = (annotation: ReviewAnnotation) => {
  if (isSwitch(annotation)) return annotation.targetAngleKey ? angleColor(annotation.targetAngleKey) : SWITCH_COLOR;
  return getAnnotationCategory(annotation.category, annotation.color).color;
};

const positive = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

/** True when the angle's end of footage is known. */
export const hasKnownLength = (angle: ReviewAngle) =>
  (positive(angle.sourceOutMs) && angle.sourceOutMs > (angle.sourceInMs || 0)) || positive(angle.durationMs);

/** End of the usable source in ms, or null when the length is unknown. */
export const sourceEndMs = (angle: ReviewAngle): number | null => {
  if (positive(angle.sourceOutMs) && angle.sourceOutMs > (angle.sourceInMs || 0)) return angle.sourceOutMs;
  return positive(angle.durationMs) ? angle.durationMs : null;
};

/**
 * The angle with its real length filled in from the media element when the
 * snapshot did not record one.
 */
export const withMeasuredDuration = (angle: ReviewAngle, measuredDurationMs?: number | null): ReviewAngle =>
  !positive(angle.durationMs) && positive(measuredDurationMs) ? { ...angle, durationMs: measuredDurationMs } : angle;

export const angleSourceMs = (angle: ReviewAngle, timelineMs: number) =>
  timelineMs - angle.timelineStartMs + (angle.sourceInMs || 0);

export const angleCovers = (angle: ReviewAngle, timelineMs: number) => {
  const source = angleSourceMs(angle, timelineMs);
  if (source < (angle.sourceInMs || 0)) return false;
  const end = sourceEndMs(angle);
  return end === null ? true : source <= end;
};

/**
 * Where an angle has footage on the timeline, as [start, end] in ms. An angle
 * of unknown length reports end === start (callers show it as open-ended).
 */
export const angleTimelineRange = (angle: ReviewAngle): [number, number] => {
  const end = sourceEndMs(angle);
  return [
    angle.timelineStartMs,
    angle.timelineStartMs + (end === null ? 0 : Math.max(0, end - (angle.sourceInMs || 0))),
  ];
};

/**
 * The source heard at `timelineMs`. The set's own sound (its audio angle, or
 * its separate recording) wherever it has footage; elsewhere the angle on
 * screen if it has footage there, else the first angle (A, B, C) that does;
 * else nothing. Clips placed one after another are each heard in turn this
 * way, where a single sound angle would leave the rest of them silent.
 */
export const soundSourceAt = (
  primary: ReviewAngle | null | undefined,
  angles: ReviewAngle[],
  timelineMs: number,
  onScreenKey?: string | null,
): ReviewAngle | null => {
  if (primary && angleCovers(primary, timelineMs)) return primary;
  const onScreen = onScreenKey ? angles.find((angle) => angle.angleKey === onScreenKey) : undefined;
  if (onScreen && angleCovers(onScreen, timelineMs)) return onScreen;
  return [...angles]
    .sort((a, b) => a.angleKey.localeCompare(b.angleKey))
    .find((angle) => angleCovers(angle, timelineMs)) ?? null;
};

/** The snapshot's length when known, otherwise the end of the last angle. */
export const timelineLengthMs = (angles: ReviewAngle[], snapshotDurationMs?: number | null) => {
  if (positive(snapshotDurationMs)) return snapshotDurationMs;
  return angles.reduce((max, angle) => Math.max(max, angleTimelineRange(angle)[1]), 0);
};

/**
 * The angle the creator has cut to at `timelineMs`: the target of the latest
 * SWITCH at or before that moment, or the master before the first cut.
 */
export const liveAngleAt = (
  annotations: ReviewAnnotation[],
  timelineMs: number,
  defaultKey = 'A'
): string => {
  let live = defaultKey;
  let latest = -Infinity;
  for (const annotation of annotations) {
    if (!isSwitch(annotation) || !annotation.targetAngleKey) continue;
    if (annotation.timelineTimeMs <= timelineMs && annotation.timelineTimeMs >= latest) {
      latest = annotation.timelineTimeMs;
      live = annotation.targetAngleKey;
    }
  }
  return live;
};

/** Notes that apply "now": a range containing it, or a point within `windowMs`. */
export const annotationsNear = (
  annotations: ReviewAnnotation[],
  timelineMs: number,
  windowMs = 1500
): ReviewAnnotation[] =>
  annotations.filter((annotation) => {
    const end = annotation.endTimeMs;
    if (end !== null && end !== undefined && end > annotation.timelineTimeMs) {
      return timelineMs >= annotation.timelineTimeMs && timelineMs <= end;
    }
    return Math.abs(timelineMs - annotation.timelineTimeMs) <= windowMs;
  });

/** Annotation start times, sorted and de-duplicated, for prev/next jumps. */
const stops = (annotations: ReviewAnnotation[]) =>
  Array.from(new Set(annotations.map((a) => a.timelineTimeMs))).sort((a, b) => a - b);

export const nextStopMs = (annotations: ReviewAnnotation[], timelineMs: number) =>
  stops(annotations).find((t) => t > timelineMs + 50) ?? null;

export const previousStopMs = (annotations: ReviewAnnotation[], timelineMs: number) => {
  // Within the first second after a stop, "previous" means the one before it,
  // the way a media player's back button behaves.
  const earlier = stops(annotations).filter((t) => t < timelineMs - 1000);
  return earlier.length ? earlier[earlier.length - 1] : null;
};

export const formatTimelineClock = (ms: number) => {
  const totalTenths = Math.max(0, Math.floor(ms / 100));
  const tenths = totalTenths % 10;
  const totalSeconds = Math.floor(totalTenths / 10);
  const seconds = totalSeconds % 60;
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / 3600);
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  return hours > 0 ? `${hours}:${mm}:${ss}.${tenths}` : `${mm}:${ss}.${tenths}`;
};

/** Below this an angle is left alone; above it, nudged by playback rate. */
export const DRIFT_NUDGE_MIN_S = 0.06;
/** Above this an angle is re-seeked rather than nudged. */
export const DRIFT_SEEK_S = 0.5;

/**
 * How to bring a follower angle back onto the clock.
 *  - `skip`: it is seeking or has no data to play yet; touching it now would
 *    cancel its range request and it would never start.
 *  - `seek`: far off (or a forced sync after a user seek).
 *  - `nudge`: slightly off while playing; speed it up or slow it down ≤5%.
 *  - `none`: close enough.
 */
export const driftCorrection = (
  element: { currentTime: number; seeking: boolean; readyState: number },
  targetS: number,
  { force = false, playing = false }: { force?: boolean; playing?: boolean } = {}
): { action: 'skip' | 'seek' | 'nudge' | 'none'; rate: number } => {
  if (force) return { action: 'seek', rate: 1 };
  // HAVE_FUTURE_DATA (3) or better is needed to play through.
  if (element.seeking || element.readyState < 3) return { action: 'skip', rate: 1 };
  const drift = element.currentTime - targetS;
  const distance = Math.abs(drift);
  if (distance > DRIFT_SEEK_S) return { action: 'seek', rate: 1 };
  if (playing && distance > DRIFT_NUDGE_MIN_S) {
    return { action: 'nudge', rate: drift > 0 ? 0.95 : 1.05 };
  }
  return { action: 'none', rate: 1 };
};
